import dayjs from 'dayjs';
import protobuf from 'protobufjs';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { chartTimeframeToInterval } from 'utils/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class MexcStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: string[][];
  asks: string[][];
  pair: string;
  displayDepth: number; // User requested depth (20 or 50)
  fetchDepth = 1000; // Always fetch more data
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;
  messageProto: protobuf.Type | undefined;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    fetch('/mexc-protos/protos.proto').then(async res => {
      const protoText = await res.text();

      const root = protobuf.parse(protoText).root;

      this.messageProto = root.lookupType('PushDataV3ApiWrapper');
    });

    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.displayDepth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    this.displayDepth = depth;
  }

  fetchTickerPrecision = async () => {
    try {
      const url = new URL('https://api.mexc.com/api/v3/ticker/bookTicker');
      const params = new URLSearchParams({
        symbol: this.pair,
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{ bidPrice: string }>('/api/v1/proxy', {
        params: { url: url.toString() },
      });

      let prec = 1;

      if (res.data) {
        prec = res.data.bidPrice?.split('.')?.[1]?.length ?? 1;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Mexc ticker error: ', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const url = new URL('https://api.mexc.com/api/v3/depth');
      const params = new URLSearchParams({
        symbol: this.pair,
        limit: this.fetchDepth.toString(),
      });

      url.search = params.toString();

      const res = await proxyAxios.get<{ asks: [string, string][]; bids: [string, string][] }>(
        '/api/v1/proxy',
        {
          params: { url: url.toString() },
        },
      );

      if (res.data) {
        this.asks = res.data.asks;
        this.bids = res.data.bids;

        // Apply precision and filter to display depth
        const orderbook = aggregateOrderbook(
          res.data.asks,
          res.data.bids,
          this.selectedPrecision,
          this.displayDepth,
        );

        this.onOrderbookUpdate(orderbook);

        return orderbook;
      }
    } catch (error) {
      console.log('Mexc orderbook error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
    from,
    to,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
    from?: number;
    to?: number;
  }) => {
    try {
      const innerInterval =
        cexExchangesTimeframes[interval]?.[ECexName.mexc] ??
        cexExchangesTimeframes['1m'][ECexName.mexc];
      const intervalMs = chartTimeframeToInterval(interval);
      const cappedLimit = Math.min(Math.max(limit, 1), 1000);

      const url = new URL('https://api.mexc.com/api/v3/klines');
      const params = new URLSearchParams();
      params.append('symbol', `${base}${quote}`.toUpperCase());
      params.append('interval', String(innerInterval));
      params.append('limit', cappedLimit.toString());

      const endTime = Number.isFinite(to) ? Number(to) : undefined;
      const startTime = Number.isFinite(from)
        ? Number(from)
        : endTime && intervalMs
          ? endTime - Math.max(1, cappedLimit - 1) * intervalMs
          : undefined;

      if (startTime !== undefined) {
        params.append('startTime', Math.max(0, Math.floor(startTime)).toString());
      }
      if (endTime !== undefined) {
        params.append('endTime', Math.max(0, Math.floor(endTime)).toString());
      }

      url.search = params.toString();

      const result = await proxyAxios.get<(number | string)[][]>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (result.data ?? []).map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Mexc klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api.mexc.com/api/v3/trades');
      const params = new URLSearchParams({
        symbol: `${base}${quote}`.toUpperCase(),
        limit: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<
        {
          isBuyerMaker: boolean;
          price: string;
          qty: string;
          time: string;
        }[]
      >('/api/v1/proxy', { params: { url: url.toString() } });

      return result.data?.map(item => ({
        price: item.price,
        quantity: item.qty,
        timestamp: item.time,
        side: item.isBuyerMaker ? 'sell' : 'buy',
      }));
    } catch (error) {
      console.log('Mexc trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://wbs-api.mexc.com/ws', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const intervalsMap = (_interval: number) =>
      ({
        60000: 'Min1',
        300000: 'Min5',
        900000: 'Min15',
        3600000: 'Min60',
        14400000: 'Hour4',
        86400000: 'Day1',
        604800000: 'Week1',
      })[_interval] ?? _interval;

    const userTimezone = dayjs.tz.guess();
    const offsetHours = dayjs().tz(userTimezone).utcOffset() / 60;
    const formattedTimezone = `UTC${offsetHours >= 0 ? '+' : ''}${offsetHours}`;

    const tickerEvent = `spot@public.miniTicker.v3.api.pb@${this.pair}@${formattedTimezone}`;
    const klineEvent = `spot@public.kline.v3.api.pb@${this.pair}@${intervalsMap(interval)}`;
    const depthEvent = `spot@public.aggre.depth.v3.api.pb@100ms@${this.pair}`;
    const tradeEvent = `spot@public.aggre.deals.v3.api.pb@100ms@${this.pair}`;

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send(JSON.stringify({ method: 'PING' }));
      }
    }, 60 * 1000);

    this.ws.onopen = () => {
      ws.send(
        JSON.stringify({
          method: 'SUBSCRIPTION',
          params: [depthEvent, klineEvent, tradeEvent, tickerEvent],
        }),
      );
    };

    this.ws.onmessage = async event => {
      try {
        if (!this.messageProto) return;

        if (event.data instanceof Blob) {
          const arrayBuffer = await event.data.arrayBuffer();
          const buffer = new Uint8Array(arrayBuffer);

          const messBuff = this.messageProto.decode(buffer) ?? undefined;
          const message = messBuff
            ? this.messageProto.toObject(messBuff, {
                longs: String,
                enums: String,
                bytes: String,
              })
            : undefined;

          if (!message) return;

          if (message.channel === klineEvent) {
            const { volume, closingPrice, highestPrice, lowestPrice, openingPrice, windowStart } =
              message.publicSpotKline;

            this.klineUpdate([
              Number(windowStart),
              openingPrice,
              highestPrice,
              lowestPrice,
              closingPrice,
              volume,
            ]);
          }

          if (message.channel === depthEvent) {
            const { asks: a, bids: b } = message.publicAggreDepths;

            const respAsks = ((a ?? []) as any[]).map(el => [el.price, el.quantity]) as string[][];
            const respBids = ((b ?? []) as any[]).map(el => [el.price, el.quantity]) as string[][];

            const asks = mergeOrderBook(this.asks, respAsks)
              .sort(sortOrderbookFunc)
              .slice(0, this.fetchDepth);
            const bids = mergeOrderBook(this.bids, respBids)
              .sort(sortOrderbookFunc)
              .slice(-this.fetchDepth);

            this.bids = bids;
            this.asks = asks;

            const aggregatedOrderbook = aggregateOrderbook(
              asks,
              bids,
              this.selectedPrecision,
              this.displayDepth,
            );

            this.onOrderbookUpdate(aggregatedOrderbook);
          }

          if (message.channel === tradeEvent) {
            const { price, quantity, time, tradeType } = message.publicAggreDeals?.dealsList?.[0];

            this.onTradeUpdate({
              price,
              quantity: quantity,
              timestamp: time,
              side: tradeType === 1 ? 'buy' : 'sell',
            });
          }

          if (message.channel === tickerEvent) {
            const { volume, rate, price, high, low, quantity } = message.publicMiniTicker;

            this.onTickerUpdate({
              baseVolume: quantity,
              change: normalizeNumber(Number(rate) * 100),
              close: price,
              high: high,
              last: Number(price).toFixed(this.precision),
              low: low,
              quoteVolume: volume,
            });
          }
        }
      } catch (error) {
        console.log('Mexc socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.displayDepth),
    );
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new MexcStrategy(args);

    await instance.fetch();

    return instance;
  };
}
